import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";

const heroImg =
  "https://customer-assets.emergentagent.com/job_ssg-coaching/artifacts/pkgt7i93_489F0D9A-2A77-47CF-B54B-18E9E2CA80E0.png";

export const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen pt-32 pb-20 lg:pb-28 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8"
          >
            <span className="ssg-hero-rule" />
            <span
              data-testid="hero-overline"
              className="text-[11px] tracking-[0.28em] uppercase font-bold text-[#9B1C1C]"
            >
              Smith Strategic Group
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            data-testid="hero-headline"
            className="font-display text-[44px] sm:text-6xl lg:text-7xl xl:text-[88px] leading-[0.98] tracking-tight font-black text-neutral-950"
          >
            Clarity<span className="text-[#9B1C1C]">.</span><br />
            Strategy<span className="text-[#9B1C1C]">.</span><br />
            <span className="text-[#9B1C1C]">Execution.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-subheadline"
            className="mt-8 max-w-xl text-base md:text-lg text-neutral-600 leading-relaxed font-medium"
          >
            Smith Strategic Group helps leaders and organizations sharpen their
            message, strengthen their systems, and move forward with clarity,
            confidence, and purpose.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#9B1C1C] text-white px-7 py-4 text-[11px] tracking-[0.24em] uppercase font-bold transition-all duration-300"
            >
              Work With Me
              <ArrowUpRight size={16} strokeWidth={1.6} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("services")}
              className="group inline-flex items-center justify-center gap-3 border border-neutral-900 text-neutral-900 px-7 py-4 text-[11px] tracking-[0.24em] uppercase font-bold hover:bg-neutral-50 transition-all duration-300"
            >
              Explore Services
              <ArrowRight size={16} strokeWidth={1.6} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 flex items-center gap-8 text-[11px] tracking-[0.2em] uppercase font-semibold text-neutral-500"
          >
            <span>Performance</span>
            <span className="w-1 h-1 bg-neutral-400 rounded-full" />
            <span>Strategy</span>
            <span className="w-1 h-1 bg-neutral-400 rounded-full" />
            <span>Leadership</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.0, ease: "easeOut" }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#0A0A0A]">
            <img
              src={heroImg}
              alt="Smith Strategic Group"
              className="w-full h-full object-contain p-10 md:p-14"
              data-testid="hero-image"
            />
            <div className="absolute -bottom-px -right-px h-24 w-24 bg-[#9B1C1C] hidden sm:block" />
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between text-white/60">
              <span className="text-[10px] tracking-[0.3em] uppercase font-bold">SSG / 01</span>
              <span className="text-[10px] tracking-[0.3em] uppercase font-bold">Est. 2024</span>
            </div>
          </div>
          <div className="hidden lg:block absolute -left-10 top-1/3 -rotate-90 origin-left text-[10px] tracking-[0.4em] uppercase text-neutral-400 font-bold">
            Strategy &nbsp;·&nbsp; Coaching &nbsp;·&nbsp; Communications
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
